import { useAccount } from 'wagmi';
import { useChainId } from 'wagmi';
import { useQueryClient, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { getBalance, GetBalanceReturnType } from '@wagmi/core';
import { wagmiConfig } from '@/onchain/OnchainProviders';
import { AssetConfig } from '@/onchain/assets';

export type AccountBalanceTokenMap<T extends readonly string[]> = Record<
  T[number],
  GetBalanceReturnType
>;

export const QUERY_KEY_ACCOUNT_BALANCE = (
  chainId: number,
  address?: `0x${string}`,
  symbol?: string,
) => ['useAccountBalance', chainId, address, symbol];

const QUERY_FN_ACCOUNT_BALANCE = async function queryFnAccountBalance<T extends AssetConfig>(
  chainId: number,
  address: `0x${string}` | undefined,
  assetConfig?: T,
): Promise<T extends `0x${string}` ? GetBalanceReturnType : null> {
  // This is a escape hatch for the case where the token address is undefined or null
  if (assetConfig === undefined || assetConfig === null) {
    return null as T extends `0x${string}` ? GetBalanceReturnType : null;
  }
  if (!chainId) {
    throw new Error('Chain ID is required');
  }
  if (!address) {
    throw new Error('Account address is required');
  }

  // Fetch the pool and position info for a given position token id
  const tokenBalance = await getBalance(wagmiConfig, {
    address,
    chainId,
    token: assetConfig.isNative ? undefined : (assetConfig.address[chainId] as `0x${string}`),
  });

  return tokenBalance as T extends `0x${string}` ? GetBalanceReturnType : null;
};

/**
 * Fetch the balance of a token for a given account
 * @param assetConfig - The asset config of the token
 * @returns The balance of the token
 */
export function useAccountBalance<T extends AssetConfig | undefined>(
  assetConfig: T,
): T extends AssetConfig ? GetBalanceReturnType : null {
  const account = useAccount();

  const chainId = useChainId();

  const queryOptions = useMemo(
    () => ({
      queryKey: QUERY_KEY_ACCOUNT_BALANCE(
        chainId,
        account.address,
        assetConfig?.displaySymbol ?? '',
      ),
      queryFn: async () => QUERY_FN_ACCOUNT_BALANCE(chainId, account.address, assetConfig),
    }),
    [chainId, account.address, assetConfig],
  );

  const { data: accountBalance } = useSuspenseQuery(queryOptions);

  return accountBalance as T extends AssetConfig ? GetBalanceReturnType : null;
}

/**
 * Fetch the balances of multiple assets for a given account
 * @param assetsConfigs - The asset configs of the assets to fetch
 * @returns The balances of each asset
 */
export function useAccountBalances(assetsConfigs: AssetConfig[]) {
  const account = useAccount();

  const chainId = useChainId();

  const queryOptions = useMemo(
    () => ({
      queries: assetsConfigs.map((assetConfig) => ({
        queryKey: QUERY_KEY_ACCOUNT_BALANCE(chainId, account.address, assetConfig.displaySymbol),
        queryFn: async () => QUERY_FN_ACCOUNT_BALANCE(chainId, account.address, assetConfig),
      })),
      combine: (results: { data: GetBalanceReturnType | null }[]) =>
        results.map((result) => result?.data),
    }),
    [account.address, chainId, assetsConfigs],
  );

  const accountBalances = useSuspenseQueries(queryOptions);

  const accountBalancesMap = useMemo(
    () =>
      assetsConfigs.reduce(
        (acc, assetConfig, i) => {
          acc[assetConfig.displaySymbol] = accountBalances[i] as GetBalanceReturnType;
          return acc;
        },
        {} as AccountBalanceTokenMap<string[]>,
      ),
    [accountBalances, assetsConfigs],
  );

  return accountBalancesMap;
}

/**
 * Reset the balance of a token for a given account
 * @param assetConfig - The asset config of the token
 */
export function useResetAccountBalance() {
  const account = useAccount();

  const chainId = useChainId();

  const queryClient = useQueryClient();

  const resetAccountBalance = useCallback(
    async (assetConfig: AssetConfig) => {
      await queryClient.resetQueries({
        queryKey: QUERY_KEY_ACCOUNT_BALANCE(
          chainId,
          account.address,
          assetConfig.displaySymbol,
        ).filter((k) => k),
      });
    },
    [queryClient, chainId, account.address],
  );

  return resetAccountBalance;
}
